import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";

import { brandsService } from "api/services/brands.service";
import { categoriesService } from "api/services/categories.service";
import { productsService } from "api/services/products.service";
import Button from "components/UI/Button";
import { Select } from "components/UI/Input";
import RichTextEditor from "components/UI/RichTextEditor";

import type { Brand } from "api/services/brands.service";
import type { Category } from "api/types/category.types";
import type {
  ProductImage,
  ProductVariant,
  ShippingPolicy,
  ReturnExchangePolicy,
  AfterServicePolicy,
  VariantStockUpdate,
} from "api/types/product.types";
import "./ProductRegistration.scss";

// ─── Types ───────────────────────────────────────────────────────────────────
type DescType = "editor" | "image";

interface OptionValue {
  id: number;
  value: string;
  price_delta: number;
}

interface OptionGroup {
  id: number;
  name: string;
  values: OptionValue[];
}

// 서버 전송용 옵션 형태 (id 없음) — 변경 비교/전송에 사용
interface OptionPayload {
  name: string;
  values: { value: string; price_delta: number }[];
}

const toOptionsPayload = (opts: OptionGroup[]): OptionPayload[] | undefined =>
  opts.length > 0
    ? opts.map((o) => ({
        name: o.name,
        values: o.values.map((v) => ({ value: v.value, price_delta: v.price_delta })),
      }))
    : undefined;

interface Policies {
  shipping_policies: ShippingPolicy[];
  return_exchange_policies: ReturnExchangePolicy[];
  after_service_policies: AfterServicePolicy[];
}

// ─── 섹션 헤더 ────────────────────────────────────────────────────────────────
const SectionTitle: React.FC<{ title: string; description?: string }> = ({
  title,
  description,
}) => (
  <div className="pr-section__title">
    <h3>{title}</h3>
    {description && <p>{description}</p>}
  </div>
);

// ─── 폼 필드 래퍼 ─────────────────────────────────────────────────────────────
const Field: React.FC<{
  label: string;
  required?: boolean;
  hint?: string;
  children: React.ReactNode;
}> = ({ label, required, hint, children }) => (
  <div className="pr-field">
    <div className="pr-field__label">
      {label}
      {required && <span className="pr-field__required">*</span>}
    </div>
    <div className="pr-field__control">
      {children}
      {hint && <p className="pr-field__hint">{hint}</p>}
    </div>
  </div>
);

// ─── 라디오 ───────────────────────────────────────────────────────────────────
const RadioGroup: React.FC<{
  name: string;
  value: string;
  options: { value: string; label: string; description?: string }[];
  onChange: (v: string) => void;
}> = ({ name, value, options, onChange }) => (
  <div className="pr-radio-group">
    {options.map((opt) => (
      <label
        key={opt.value}
        className={`pr-radio ${value === opt.value ? "pr-radio--active" : ""}`}
      >
        <input
          type="radio"
          name={name}
          value={opt.value}
          checked={value === opt.value}
          onChange={() => onChange(opt.value)}
        />
        <span className="pr-radio__dot" />
        <span className="pr-radio__label">{opt.label}</span>
        {opt.description && <span className="pr-radio__desc">{opt.description}</span>}
      </label>
    ))}
  </div>
);

// ─── 메인 컴포넌트 ─────────────────────────────────────────────────────────────
const ProductEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const productId = Number(id);
  const navigate = useNavigate();
  const onBack = () => navigate("/product-list");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  // 기본 정보
  const [productName, setProductName] = useState("");
  const [brand, setBrand] = useState("");
  const [brands, setBrands] = useState<Brand[]>([]);
  const [brandsLoading, setBrandsLoading] = useState(true);
  const [cat1, setCat1] = useState<number | "">("");
  const [cat2, setCat2] = useState<number | "">("");
  const [cat3, setCat3] = useState<number | "">("");

  // 가격
  const [price, setPrice] = useState("");
  const [discountPrice, setDiscountPrice] = useState("");
  const [discountRate, setDiscountRate] = useState(0);

  // 재고
  const [stock, setStock] = useState("");
  const [isUnlimitedStock, setIsUnlimitedStock] = useState(false);
  const [lowStockThreshold, setLowStockThreshold] = useState(5);

  // 상태
  const [isActive, setIsActive] = useState(true);
  const [isFeatured, setIsFeatured] = useState(false);
  const [isDigital, setIsDigital] = useState(false);

  // 설명
  const [descType, setDescType] = useState<DescType>("editor");
  const [description, setDescription] = useState("");

  // 태그
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState("");

  // 이미지 (읽기 전용 — 수정 불가)
  const descImageInputRef = useRef<HTMLInputElement>(null);
  const [existingImages, setExistingImages] = useState<ProductImage[]>([]);
  const [descImages, setDescImages] = useState<File[]>([]);
  const [descImagePreviews, setDescImagePreviews] = useState<string[]>([]);

  // 옵션
  const [hasOptions, setHasOptions] = useState(false);
  const [options, setOptions] = useState<OptionGroup[]>([]);

  // 사용자 입력 옵션 (구매자가 직접 입력하는 항목의 제목 목록)
  const [customerInputOptions, setCustomerInputOptions] = useState<string[]>([]);

  // 재고 관리 (옵션 조합 = variant)
  const [variants, setVariants] = useState<ProductVariant[]>([]);
  const [variantStock, setVariantStock] = useState<Record<number, number>>({});
  const [variantActive, setVariantActive] = useState<Record<number, boolean>>({});
  const [showStockManager, setShowStockManager] = useState(false);
  const [stockSaving, setStockSaving] = useState(false);
  const initialVariantStock = useRef<Record<number, number>>({});
  const initialVariantActive = useRef<Record<number, boolean>>({});

  // 정책
  const [policies, setPolicies] = useState<Policies>({
    shipping_policies: [],
    return_exchange_policies: [],
    after_service_policies: [],
  });
  const [policiesLoading, setPoliciesLoading] = useState(true);
  const [shippingPolicyId, setShippingPolicyId] = useState<number | null>(null);
  const [returnPolicyId, setReturnPolicyId] = useState<number | null>(null);
  const [afterServiceId, setAfterServiceId] = useState<number | null>(null);

  // 카테고리
  const [allCategories, setAllCategories] = useState<Category[]>([]);

  // 초기값 저장 (변경된 필드만 전송하기 위해)
  interface InitialProductValues {
    name: string;
    brand: string;
    detailed_content: string;
    price: string;
    discount_price: string | null;
    stock_quantity: number;
    is_unlimited_stock: boolean;
    low_stock_threshold: number;
    is_active: boolean;
    is_featured: boolean;
    is_digital: boolean;
    shipping_policy_id: number | null;
    return_exchange_policy_id: number | null;
    after_service_id: number | null;
    category_ids: number[];
    options: OptionPayload[] | undefined;
    customer_input_options: string[];
  }
  const initialValues = useRef<InitialProductValues | null>(null);

  // 제출
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  // ─── 카테고리 경로 탐색 ──────────────────────────────────────────────────────
  const findCategoryPath = (cats: Category[], targetId: number): number[] | null => {
    for (const cat of cats) {
      if (cat.id === targetId) return [cat.id];
      if (cat.children?.length) {
        const path = findCategoryPath(cat.children, targetId);
        if (path) return [cat.id, ...path];
      }
    }
    return null;
  };

  // ─── 정책 로드 ──────────────────────────────────────────────────────────────
  useEffect(() => {
    setPoliciesLoading(true);
    productsService
      .getPolicyOptions()
      .then((res) => {
        setPolicies(res.data);
      })
      .catch(() => {
        console.error("정책 목록을 불러오는데 실패했습니다.");
      })
      .finally(() => {
        setPoliciesLoading(false);
      });
  }, []);

  // ─── 브랜드 목록 로드 ────────────────────────────────────────────────────────
  useEffect(() => {
    setBrandsLoading(true);
    brandsService
      .list()
      .then((res) => {
        setBrands(res.data);
      })
      .catch(() => {
        console.error("브랜드 목록을 불러오는데 실패했습니다.");
      })
      .finally(() => {
        setBrandsLoading(false);
      });
  }, []);

  // ─── 상품 데이터 로드 ────────────────────────────────────────────────────────
  useEffect(() => {
    setLoading(true);
    Promise.all([productsService.get(productId), categoriesService.listAll()])
      .then(([productRes, catRes]) => {
        const product = productRes.data;
        const cats: Category[] = Array.isArray(catRes.data)
          ? catRes.data
          : ((catRes.data as { results?: Category[] }).results ?? []);
        setAllCategories(cats);

        // 기본 정보
        setProductName(product.name);
        setBrand(product.brand || "");
        setPrice(product.price);
        setDiscountPrice(product.discount_price || "");
        setDescription(product.detailed_content || "");
        setStock(String(product.stock_quantity ?? 0));
        setIsUnlimitedStock(product.is_unlimited_stock);
        setLowStockThreshold(product.low_stock_threshold ?? 5);
        setIsActive(product.is_active);
        setIsFeatured(product.is_featured);
        setIsDigital(product.is_digital);

        // 정책
        if (product.shipping_policy) setShippingPolicyId(product.shipping_policy.id);
        if (product.return_exchange_policy) setReturnPolicyId(product.return_exchange_policy.id);
        if (product.after_service) setAfterServiceId(product.after_service.id);

        // 카테고리: 가장 깊은 카테고리로부터 경로 탐색
        if (product.categories?.length) {
          const sorted = [...product.categories].sort((a, b) => (b.depth ?? 0) - (a.depth ?? 0));
          const leafCat = sorted[0];
          const path = findCategoryPath(cats, leafCat.id);
          if (path) {
            if (path[0] !== undefined) setCat1(path[0]);
            if (path[1] !== undefined) setCat2(path[1]);
            if (path[2] !== undefined) setCat3(path[2]);
          }
        }

        // 이미지
        setExistingImages(product.images || []);

        // 옵션
        const loadedOptions: OptionGroup[] =
          product.product_options?.length > 0
            ? product.product_options.map((opt, i) => ({
                id: i,
                name: opt.group_name ?? "",
                values: (opt.values ?? []).map((v, vi) => ({
                  id: vi,
                  value: v.value,
                  price_delta: Number(v.price_delta) || 0,
                })),
              }))
            : [];
        if (loadedOptions.length > 0) {
          setHasOptions(true);
          setOptions(loadedOptions);
        }

        // 사용자 입력 옵션
        const loadedCustomerInputOptions = product.customer_input_options ?? [];
        setCustomerInputOptions(loadedCustomerInputOptions);

        // 재고 관리: 옵션 조합(variant)
        // 상품 상세 GET은 백엔드에서 1시간 캐시되어 재고가 stale일 수 있으므로,
        // 우선 상세값으로 표시한 뒤 캐시 없는 variants 엔드포인트로 최신값을 덮어쓴다.
        applyVariants(product.variants || []);
        if (product.variants?.length) {
          productsService
            .getVariants(productId)
            .then((res) => applyVariants(res.data.variants ?? []))
            .catch(() => {
              /* 실패 시 상세 응답의 variants 값을 유지 */
            });
        }

        // 카테고리 ID 계산
        const initLeafCatId = product.categories?.length
          ? (() => {
              const sorted = [...product.categories].sort(
                (a, b) => (b.depth ?? 0) - (a.depth ?? 0),
              );
              const path = findCategoryPath(cats, sorted[0].id);
              return path ? (path[path.length - 1] ?? null) : null;
            })()
          : null;

        // 초기값 저장
        initialValues.current = {
          name: product.name,
          brand: product.brand || "",
          detailed_content: product.detailed_content || "",
          price: product.price,
          discount_price: product.discount_price || null,
          stock_quantity: product.stock_quantity ?? 0,
          is_unlimited_stock: product.is_unlimited_stock,
          low_stock_threshold: product.low_stock_threshold ?? 5,
          is_active: product.is_active,
          is_featured: product.is_featured,
          is_digital: product.is_digital,
          shipping_policy_id: product.shipping_policy?.id ?? null,
          return_exchange_policy_id: product.return_exchange_policy?.id ?? null,
          after_service_id: product.after_service?.id ?? null,
          category_ids: initLeafCatId != null ? [initLeafCatId] : [],
          // 전송 형태(id 없음)로 저장해야 currentValues와 정확히 비교됨
          options: toOptionsPayload(loadedOptions),
          customer_input_options: loadedCustomerInputOptions,
        };

        setLoading(false);
      })
      .catch(() => {
        setLoadError("상품 정보를 불러오는데 실패했습니다.");
        setLoading(false);
      });
    // 상품 로드는 productId 변경 시에만 수행 (모듈 스코프 헬퍼/세터는 안정적)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  // ─── 할인율 자동 계산 ───────────────────────────────────────────────────────
  useEffect(() => {
    const p = Number(price);
    const dp = Number(discountPrice);
    if (p > 0 && dp >= 0) {
      setDiscountRate(Math.max(0, Math.round(((p - dp) / p) * 100)));
    } else {
      setDiscountRate(0);
    }
  }, [price, discountPrice]);

  const depth0 = allCategories ?? [];
  const depth1: Category[] = depth0.find((c) => c.id === cat1)?.children ?? [];
  const depth2: Category[] = depth1.find((c) => c.id === cat2)?.children ?? [];

  // 상품 설명 에디터 이미지 업로드 → 서버 URL 반환
  const uploadDetailImage = async (file: File): Promise<string> => {
    const { data } = await productsService.uploadImage(file);
    return data.url;
  };

  const handleAddDescImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const combined = [...descImages, ...files].slice(0, 10);
    setDescImages(combined);
    setDescImagePreviews(combined.map((f) => URL.createObjectURL(f)));
    e.target.value = "";
  };

  const handleRemoveDescImage = (idx: number) => {
    URL.revokeObjectURL(descImagePreviews[idx]);
    setDescImages((prev) => prev.filter((_, i) => i !== idx));
    setDescImagePreviews((prev) => prev.filter((_, i) => i !== idx));
  };

  // ─── 태그 핸들러 ────────────────────────────────────────────────────────────
  const handleAddTag = () => {
    const trimmed = tagInput.trim();
    if (!trimmed || tags.includes(trimmed)) return;
    setTags((prev) => [...prev, trimmed]);
    setTagInput("");
  };

  const handleTagKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleRemoveTag = (tag: string) => {
    setTags((prev) => prev.filter((t) => t !== tag));
  };

  // ─── 옵션 핸들러 ────────────────────────────────────────────────────────────
  const addOption = () => {
    setOptions((prev) => [
      ...prev,
      { id: Date.now(), name: "", values: [{ id: Date.now() + 1, value: "", price_delta: 0 }] },
    ]);
  };

  const removeOption = (id: number) => {
    setOptions((prev) => prev.filter((o) => o.id !== id));
  };

  const updateOptionName = (id: number, name: string) => {
    setOptions((prev) => prev.map((o) => (o.id === id ? { ...o, name } : o)));
  };

  const addOptionValue = (optId: number) => {
    setOptions((prev) =>
      prev.map((o) =>
        o.id === optId
          ? { ...o, values: [...o.values, { id: Date.now(), value: "", price_delta: 0 }] }
          : o,
      ),
    );
  };

  const removeOptionValue = (optId: number, valId: number) => {
    setOptions((prev) =>
      prev.map((o) =>
        o.id === optId ? { ...o, values: o.values.filter((v) => v.id !== valId) } : o,
      ),
    );
  };

  const updateOptionValue = (
    optId: number,
    valId: number,
    field: "value" | "price_delta",
    val: string | number,
  ) => {
    setOptions((prev) =>
      prev.map((o) =>
        o.id === optId
          ? { ...o, values: o.values.map((v) => (v.id === valId ? { ...v, [field]: val } : v)) }
          : o,
      ),
    );
  };

  // ─── 사용자 입력 옵션 핸들러 ─────────────────────────────────────────────────
  const addCustomerInputOption = () => {
    setCustomerInputOptions((prev) => [...prev, ""]);
  };

  const updateCustomerInputOption = (index: number, value: string) => {
    setCustomerInputOptions((prev) => prev.map((t, i) => (i === index ? value : t)));
  };

  const removeCustomerInputOption = (index: number) => {
    setCustomerInputOptions((prev) => prev.filter((_, i) => i !== index));
  };

  // ─── 재고 관리(variant) 핸들러 ─────────────────────────────────────────────
  // variant 목록을 상태/초기값에 반영
  const applyVariants = (list: ProductVariant[]) => {
    setVariants(list);
    const stockMap: Record<number, number> = {};
    const activeMap: Record<number, boolean> = {};
    list.forEach((v) => {
      stockMap[v.id] = v.stock_quantity;
      activeMap[v.id] = v.is_active;
    });
    setVariantStock(stockMap);
    setVariantActive(activeMap);
    initialVariantStock.current = { ...stockMap };
    initialVariantActive.current = { ...activeMap };
  };

  // 옵션 조합 라벨 (예: "M / 블랙")
  const variantLabel = (v: ProductVariant) =>
    v.option_values.map((ov) => ov.value).join(" / ") || v.sku;

  const handleVariantStockChange = (variantId: number, value: string) => {
    const next = Math.max(0, Number(value.replace(/[^0-9]/g, "")) || 0);
    setVariantStock((prev) => ({ ...prev, [variantId]: next }));
  };

  const toggleVariantActive = (variantId: number, active: boolean) => {
    setVariantActive((prev) => ({ ...prev, [variantId]: active }));
  };

  const handleSaveStock = async () => {
    const updates: VariantStockUpdate[] = variants
      .map((v) => {
        const u: VariantStockUpdate = { variant_id: v.id };
        if (variantStock[v.id] !== initialVariantStock.current[v.id])
          u.stock_quantity = variantStock[v.id];
        if (variantActive[v.id] !== initialVariantActive.current[v.id])
          u.is_active = variantActive[v.id];
        return u;
      })
      .filter((u) => u.stock_quantity !== undefined || u.is_active !== undefined);

    if (updates.length === 0) {
      alert("변경된 재고가 없습니다.");
      return;
    }

    setStockSaving(true);
    try {
      const { data } = await productsService.updateVariantStock(productId, updates);
      // 백엔드는 일부 실패 시 207을 반환하고 errors/updated_count를 본문에 담는다.
      // 2xx여도 실제 반영 건수를 확인해야 "저장됐다고 떴는데 안 바뀜"을 방지할 수 있다.
      if (data.errors && data.errors.length > 0) {
        console.error("variant 재고 업데이트 일부 실패:", data.errors);
      }

      // 백엔드가 재고 0 → 자동 품절 처리하므로, 최신 상태를 다시 불러와 반영
      const res = await productsService.getVariants(productId);
      applyVariants(res.data.variants ?? []);

      if (!data.updated_count) {
        alert(
          "재고가 저장되지 않았습니다. 서버에서 변경된 항목이 없습니다.\n(콘솔의 오류 내용을 확인해주세요)",
        );
      } else {
        alert(`재고가 저장되었습니다. (${data.updated_count}건)`);
      }
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      alert(
        status === 403 ? "재고를 수정할 권한이 없습니다." : "재고 저장 중 오류가 발생했습니다.",
      );
    } finally {
      setStockSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await productsService.remove(productId);
      alert("상품은 비활성화 처리되며, 언제든 판매여부 값을 변경하여 다시 노출할 수 있습니다");
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      alert(
        status === 403 ? "상품을 삭제할 권한이 없습니다." : "상품을 비활성화 처리할 수 없습니다.",
      );
    } finally {
      navigate(-1);
    }
  };
  // ─── 제출 (변경된 필드만 전송) ───────────────────────────────────────────────
  const handleSubmit = async (isDraft = false) => {
    if (!productName) {
      setSubmitError("상품명을 입력해주세요.");
      return;
    }
    if (!price || Number(price) <= 0) {
      setSubmitError("정가를 입력해주세요.");
      return;
    }
    setSubmitting(true);
    setSubmitError("");
    try {
      const leafCatId = (cat3 || cat2 || cat1) as number | "";
      const categoryIds = leafCatId !== "" ? [leafCatId] : [];
      const currentOptionsPayload = hasOptions ? toOptionsPayload(options) : undefined;

      const currentValues: Record<string, unknown> = {
        name: productName,
        brand,
        detailed_content: description,
        price,
        discount_price: discountPrice || null,
        // 무제한 재고면 stock_quantity는 전송하지 않음(undefined → 비교/전송에서 제외)
        stock_quantity: isUnlimitedStock ? undefined : stock ? Number(stock) : 0,
        is_unlimited_stock: isUnlimitedStock,
        low_stock_threshold: lowStockThreshold,
        // 임시저장이면 비활성, 아니면 "판매 여부" Select 값을 그대로 사용
        is_active: isDraft ? false : isActive,
        is_featured: isFeatured,
        is_digital: isDigital,
        shipping_policy_id: shippingPolicyId,
        return_exchange_policy_id: returnPolicyId,
        after_service_id: afterServiceId,
        category_ids: categoryIds,
        options: currentOptionsPayload,
        customer_input_options: customerInputOptions.map((t) => t.trim()).filter((t) => t !== ""),
      };

      // 초기값과 비교하여 변경된 필드만 추출
      const payload: Record<string, unknown> = {};
      const init = initialValues.current;
      if (init) {
        for (const key of Object.keys(currentValues)) {
          const cur = currentValues[key];
          // undefined 값은 비교/전송 대상에서 제외 (JSON.stringify(undefined) 비교 깨짐 방지)
          if (cur === undefined) continue;
          const orig = (init as unknown as Record<string, unknown>)[key];
          if (JSON.stringify(cur) !== JSON.stringify(orig)) {
            payload[key] = cur;
          }
        }
      } else {
        // 초기값이 없으면 전체 전송 (폴백)
        Object.assign(payload, currentValues);
      }

      // 백엔드 validate()가 discount_price를 price와 비교(>=)하므로, discount_price를
      // 보낼 때는 price도 함께 전송해야 한다(price 미포함 시 0과 비교되어 오검증).
      // 단, null(할인가 제거)은 백엔드에서 `None >= price` TypeError(500)를 유발하므로 제외한다.
      if ("discount_price" in payload) {
        if (payload.discount_price === null) {
          delete payload.discount_price;
        } else if (!("price" in payload)) {
          payload.price = price;
        }
      }

      if (Object.keys(payload).length === 0) {
        alert("변경된 내용이 없습니다.");
        setSubmitting(false);
        return;
      }

      await productsService.update(
        productId,
        payload as unknown as Parameters<typeof productsService.update>[1],
      );
      alert("상품이 수정되었습니다!");
      onBack();
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 403) {
        setSubmitError("상품을 수정할 권한이 없습니다.");
      } else {
        setSubmitError("상품 수정 중 오류가 발생했습니다.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ─── 로딩 / 에러 ─────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="product-registration">
        <div style={{ padding: 80, textAlign: "center", color: "#71717A" }}>
          상품 정보를 불러오는 중...
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="product-registration">
        <div style={{ padding: 80, textAlign: "center" }}>
          <p style={{ color: "#EF4444", marginBottom: 16 }}>{loadError}</p>
          <Button variant="secondary" onClick={onBack}>
            목록으로 돌아가기
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="product-registration">
      {/* ── 상단 헤더 ─────────────────────────────────────────── */}
      <div className="pr-header">
        <div className="pr-header__left">
          <h1 className="pr-header__title">{productName || "상품 수정"}</h1>
          <p className="pr-header__breadcrumb">상품 관리 › 상품 목록 › 상품 수정</p>
        </div>
        <div className="pr-header__actions">
          <Button variant="outline" disabled={submitting} onClick={onBack}>
            목록으로
          </Button>
          {/* <Button variant='outline' disabled={submitting} onClick={() => handleSubmit(true)}>임시저장</Button> */}
          <Button variant="primary" disabled={submitting} onClick={() => handleSubmit(false)}>
            {submitting ? "처리 중..." : "수정 완료"}
          </Button>
        </div>
      </div>
      {submitError && (
        <p style={{ color: "#EF4444", padding: "8px 24px", margin: 0 }}>{submitError}</p>
      )}

      <div className="pr-body">
        {/* ── 기본 정보 ─────────────────────────────────────────── */}
        <section className="pr-section">
          <SectionTitle title="기본 정보" />

          <Field label="상품명" required hint="최대 40자까지 입력 가능합니다.">
            <div className="pr-input-with-count">
              <input
                className="pr-input"
                placeholder="상품명을 입력해주세요"
                value={productName}
                maxLength={40}
                onChange={(e) => setProductName(e.target.value)}
              />
              <span className="pr-input-count">{productName.length}/40</span>
            </div>
          </Field>

          <Field label="브랜드">
            <Select
              value={brand}
              onChange={(e) => setBrand(e.target.value)}
              disabled={brandsLoading}
              options={[
                { value: "", label: brandsLoading ? "불러오는 중..." : "브랜드 선택" },
                // 현재 상품의 브랜드가 활성 브랜드 목록에 없으면(비활성/구 데이터) 값 유실 방지
                ...(brand && !brands.some((b) => b.name === brand)
                  ? [{ value: brand, label: brand }]
                  : []),
                ...brands.map((b) => ({ value: b.name, label: b.name })),
              ]}
            />
          </Field>

          <Field label="카테고리" required>
            <div className="pr-category">
              <Select
                value={String(cat1)}
                onChange={(e) => {
                  setCat1(e.target.value ? Number(e.target.value) : "");
                  setCat2("");
                  setCat3("");
                }}
                options={[
                  { value: "", label: "대분류 선택" },
                  ...depth0.map((c) => ({ value: String(c.id), label: c.name })),
                ]}
                style={{ flex: 1 }}
              />
              <Select
                value={String(cat2)}
                onChange={(e) => {
                  setCat2(e.target.value ? Number(e.target.value) : "");
                  setCat3("");
                }}
                disabled={!cat1}
                options={[
                  { value: "", label: "중분류 선택" },
                  ...depth1.map((c) => ({ value: String(c.id), label: c.name })),
                ]}
                style={{ flex: 1 }}
              />
              {depth2.length > 0 && (
                <Select
                  value={String(cat3)}
                  onChange={(e) => setCat3(e.target.value ? Number(e.target.value) : "")}
                  disabled={!cat2}
                  options={[
                    { value: "", label: "소분류 선택" },
                    ...depth2.map((c) => ({ value: String(c.id), label: c.name })),
                  ]}
                  style={{ flex: 1 }}
                />
              )}
            </div>
          </Field>

          {/* ── 상품 이미지 (수정 불가) ── */}
          <Field label="상품 이미지" hint="이미지 수정은 현재 지원하지 않습니다.">
            <div className="pr-image-upload">
              {existingImages.map((img, i) => (
                <div key={img.id} className="pr-image-upload__item pr-image-upload__item--filled">
                  <img
                    src={img.image}
                    alt={`상품이미지${i + 1}`}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                  {img.is_thumbnail && (
                    <span
                      style={{
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        background: "rgba(99,102,241,0.85)",
                        color: "#fff",
                        fontSize: 10,
                        textAlign: "center",
                        padding: "2px 0",
                      }}
                    >
                      썸네일
                    </span>
                  )}
                </div>
              ))}
            </div>
          </Field>

          <Field label="상품 태그">
            <div className="pr-tag-input-row">
              <input
                className="pr-input"
                placeholder="태그 입력 후 Enter 또는 추가 버튼 클릭"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={handleTagKeyDown}
                style={{ flex: 1 }}
              />
              <Button variant="outline" size="sm" onClick={handleAddTag}>
                추가
              </Button>
            </div>
            {tags.length > 0 && (
              <div className="pr-tags" style={{ marginTop: 8 }}>
                {tags.map((tag) => (
                  <span key={tag} className="pr-tag pr-tag--selected">
                    {tag}
                    <button
                      type="button"
                      className="pr-tag__remove"
                      onClick={() => handleRemoveTag(tag)}
                    >
                      ✕
                    </button>
                  </span>
                ))}
              </div>
            )}
          </Field>
        </section>

        {/* ── 상품 설명 ────────────────────────────────────────── */}
        <section className="pr-section">
          <SectionTitle title="상품 설명" />

          <Field label="입력 방식">
            <RadioGroup
              name="descType"
              value={descType}
              onChange={(v) => setDescType(v as DescType)}
              options={[
                { value: "editor", label: "HTML 에디터" },
                { value: "image", label: "이미지 업로드(제공예정)" },
              ]}
            />
          </Field>

          {descType === "editor" ? (
            <Field label="상품 설명 내용" required>
              <RichTextEditor
                value={description}
                onChange={setDescription}
                uploadImage={uploadDetailImage}
                placeholder="상품 설명을 입력해주세요..."
              />
            </Field>
          ) : (
            <Field label="설명 이미지" required hint="최대 10장까지 업로드 가능합니다.">
              <div className="pr-image-upload">
                {descImagePreviews.map((src, i) => (
                  <div key={i} className="pr-image-upload__item pr-image-upload__item--filled">
                    <img
                      src={src}
                      alt={`설명이미지${i + 1}`}
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                    <button
                      className="pr-image-upload__remove"
                      type="button"
                      onClick={() => handleRemoveDescImage(i)}
                    >
                      ✕
                    </button>
                  </div>
                ))}
                {descImages.length < 10 && (
                  <div
                    className="pr-image-upload__item pr-image-upload__item--add"
                    onClick={() => descImageInputRef.current?.click()}
                  >
                    <span style={{ fontSize: 24, color: "#A1A1AA" }}>＋</span>
                    <span style={{ fontSize: 12, color: "#71717A", marginTop: 4 }}>
                      이미지 추가
                    </span>
                  </div>
                )}
              </div>
              <input
                ref={descImageInputRef}
                type="file"
                accept="image/*"
                multiple
                style={{ display: "none" }}
                onChange={handleAddDescImage}
              />
            </Field>
          )}
        </section>

        {/* ── 가격 / 옵션 설정 ─────────────────────────────────── */}
        <section className="pr-section">
          <SectionTitle
            title="가격 / 옵션 설정"
            description='단일 상품이면 "옵션 없음"을, 색상·사이즈 등 옵션이 있다면 "옵션 있음"을 선택하세요.'
          />

          <div className="pr-price-grid">
            <Field label="정가" required hint="1,000원 이상 입력">
              <div className="pr-input-suffix">
                <input
                  className="pr-input pr-input--right"
                  type="text"
                  placeholder="0"
                  value={price ? Number(price).toLocaleString("ko-KR") : ""}
                  onChange={(e) => setPrice(e.target.value.replace(/[^0-9]/g, ""))}
                />
                <span className="pr-suffix">원</span>
              </div>
            </Field>
            <Field label="판매가" required hint="정가 이하로 입력">
              <div className="pr-input-suffix">
                <input
                  className="pr-input pr-input--right"
                  type="text"
                  placeholder="0"
                  value={discountPrice ? Number(discountPrice).toLocaleString("ko-KR") : ""}
                  onChange={(e) => setDiscountPrice(e.target.value.replace(/[^0-9]/g, ""))}
                />
                <span className="pr-suffix">원</span>
              </div>
            </Field>
            <Field label="할인율 (자동 계산)">
              <div className="pr-input-suffix">
                <input
                  className="pr-input pr-input--right"
                  type="number"
                  readOnly
                  value={discountRate}
                />
                <span className="pr-suffix">%</span>
              </div>
            </Field>
          </div>

          <Field label="옵션 구성" required>
            <div className="pr-checkbox-row">
              <label className="pr-checkbox-label">
                <input
                  type="checkbox"
                  checked={hasOptions}
                  onChange={(e) => {
                    setHasOptions(e.target.checked);
                    if (!e.target.checked) setOptions([]);
                  }}
                />
                <span>옵션 사용</span>
              </label>
            </div>
          </Field>

          {!hasOptions ? (
            <div className="pr-price-grid">
              <Field label="재고 수량" required>
                <div className="pr-input-suffix">
                  <input
                    className="pr-input pr-input--right"
                    type="number"
                    placeholder="0"
                    value={stock}
                    onChange={(e) => setStock(e.target.value)}
                    disabled={isUnlimitedStock}
                  />
                  <span className="pr-suffix">개</span>
                </div>
              </Field>
              <Field label="무제한 재고">
                <div className="pr-checkbox-row">
                  <label className="pr-checkbox-label">
                    <input
                      type="checkbox"
                      checked={isUnlimitedStock}
                      onChange={(e) => setIsUnlimitedStock(e.target.checked)}
                    />
                    <span>무제한으로 설정</span>
                  </label>
                </div>
              </Field>
              <Field label="재고 알림 수량">
                <div className="pr-input-suffix">
                  <input
                    className="pr-input pr-input--right"
                    type="number"
                    placeholder="5"
                    value={lowStockThreshold}
                    onChange={(e) => setLowStockThreshold(Number(e.target.value))}
                  />
                  <span className="pr-suffix">개 미만</span>
                </div>
              </Field>
            </div>
          ) : (
            <Field
              label="옵션 설정"
              required
              hint="옵션명(예: 색상)과 옵션값(예: 빨강)을 입력하세요."
            >
              <div className="pr-option-rows">
                {options.map((opt, optIdx) => (
                  <div key={opt.id} className="pr-option-group">
                    <div className="pr-option-group__header">
                      <span className="pr-option-row__index">옵션 {optIdx + 1}</span>
                      <input
                        className="pr-input"
                        placeholder="옵션명 (예: 색상)"
                        value={opt.name}
                        onChange={(e) => updateOptionName(opt.id, e.target.value)}
                        style={{ width: 180 }}
                      />
                      <button
                        className="pr-option-row__remove"
                        type="button"
                        onClick={() => removeOption(opt.id)}
                      >
                        ✕
                      </button>
                    </div>
                    <div className="pr-option-values">
                      {opt.values.map((val) => (
                        <div key={val.id} className="pr-option-value-row">
                          <input
                            className="pr-input"
                            placeholder="옵션값 (예: 빨강)"
                            value={val.value}
                            onChange={(e) =>
                              updateOptionValue(opt.id, val.id, "value", e.target.value)
                            }
                            style={{ flex: 1 }}
                          />
                          <div className="pr-input-suffix" style={{ width: 140 }}>
                            <input
                              className="pr-input pr-input--right"
                              type="number"
                              placeholder="0"
                              value={val.price_delta}
                              onChange={(e) =>
                                updateOptionValue(
                                  opt.id,
                                  val.id,
                                  "price_delta",
                                  Number(e.target.value),
                                )
                              }
                            />
                            <span className="pr-suffix">원</span>
                          </div>
                          {opt.values.length > 1 && (
                            <button
                              className="pr-option-row__remove"
                              type="button"
                              onClick={() => removeOptionValue(opt.id, val.id)}
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      ))}
                      <Button variant="secondary" size="sm" onClick={() => addOptionValue(opt.id)}>
                        + 값 추가
                      </Button>
                    </div>
                  </div>
                ))}
                <Button variant="secondary" size="sm" onClick={addOption}>
                  + 옵션 추가
                </Button>
              </div>
            </Field>
          )}

          <Field label="사용자 입력 옵션" hint="구매자가 직접 입력할 항목의 제목을 추가하세요.">
            <div className="pr-input-options">
              {customerInputOptions.map((title, index) => (
                <div className="pr-option-row" key={index}>
                  <input
                    className="pr-input"
                    placeholder="제목을 입력해주세요 (예: 각인 문구)"
                    value={title}
                    onChange={(e) => updateCustomerInputOption(index, e.target.value)}
                    style={{ flex: 1 }}
                  />
                  <button
                    className="pr-option-row__remove"
                    type="button"
                    onClick={() => removeCustomerInputOption(index)}
                  >
                    ✕
                  </button>
                </div>
              ))}
              <Button variant="secondary" size="sm" onClick={addCustomerInputOption}>
                + 제목 추가
              </Button>
            </div>
          </Field>

          {/* ── 재고 관리 (옵션 조합별) ── */}
          {variants.length > 0 && (
            <div className="pr-stock-manager">
              <button
                type="button"
                className="pr-stock-manager__toggle"
                onClick={() => setShowStockManager((v) => !v)}
              >
                <span className="pr-stock-manager__toggle-title">
                  재고 관리
                  <span className="pr-stock-manager__count">{variants.length}개 조합</span>
                </span>
                <span className="pr-stock-manager__chevron">{showStockManager ? "▲" : "▼"}</span>
              </button>

              {showStockManager && (
                <div className="pr-stock-manager__body">
                  <div className="pr-stock-table">
                    <div className="pr-stock-table__head">
                      <span className="pr-stock-table__col pr-stock-table__col--name">
                        옵션 조합
                      </span>
                      <span className="pr-stock-table__col pr-stock-table__col--stock">
                        재고 수량
                      </span>
                      <span className="pr-stock-table__col pr-stock-table__col--active">판매</span>
                    </div>
                    {variants.map((v) => (
                      <div key={v.id} className="pr-stock-table__row">
                        <div className="pr-stock-table__col pr-stock-table__col--name">
                          <span className="pr-stock-table__label">{variantLabel(v)}</span>
                          <span className="pr-stock-table__sku">{v.sku}</span>
                        </div>
                        <div className="pr-stock-table__col pr-stock-table__col--stock">
                          <div className="pr-input-suffix">
                            <input
                              className="pr-input pr-input--right"
                              type="text"
                              inputMode="numeric"
                              value={variantStock[v.id] ?? 0}
                              onChange={(e) => handleVariantStockChange(v.id, e.target.value)}
                            />
                            <span className="pr-suffix">개</span>
                          </div>
                        </div>
                        <div className="pr-stock-table__col pr-stock-table__col--active">
                          <label className="pr-checkbox-label">
                            <input
                              type="checkbox"
                              checked={variantActive[v.id] ?? false}
                              onChange={(e) => toggleVariantActive(v.id, e.target.checked)}
                            />
                            <span>{variantActive[v.id] ? "판매중" : "중지"}</span>
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="pr-stock-manager__hint">
                    재고가 0이 되면 해당 조합은 자동으로 판매 중지됩니다.
                  </p>
                  <div className="pr-stock-manager__actions">
                    <Button
                      variant="primary"
                      size="sm"
                      disabled={stockSaving}
                      onClick={handleSaveStock}
                    >
                      {stockSaving ? "저장 중..." : "재고 저장"}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </section>

        {/* ── 상품 상태 설정 ──────────────────────────────────── */}
        <section className="pr-section">
          <SectionTitle title="상품 상태 설정" />

          <div className="pr-price-grid">
            <Field label="판매 여부" required>
              <Select
                value={String(isActive)}
                onChange={(e) => setIsActive(e.target.value === "true")}
                options={[
                  { value: "true", label: "판매 중" },
                  { value: "false", label: "판매 중지" },
                ]}
              />
            </Field>
            <Field label="대표 상품">
              <Select
                value={String(isFeatured)}
                onChange={(e) => setIsFeatured(e.target.value === "true")}
                options={[
                  { value: "false", label: "아니오" },
                  { value: "true", label: "예" },
                ]}
              />
            </Field>
            <Field label="디지털 상품">
              <Select
                value={String(isDigital)}
                onChange={(e) => setIsDigital(e.target.value === "true")}
                options={[
                  { value: "false", label: "아니오 (실물 상품)" },
                  { value: "true", label: "예 (디지털 상품)" },
                ]}
              />
            </Field>
          </div>
        </section>

        {/* ── 정책 설정 ────────────────────────────────────────── */}
        <section className="pr-section">
          <SectionTitle title="정책 설정" />

          <div className="pr-price-grid">
            <Field label="배송 정책">
              <Select
                value={String(shippingPolicyId ?? "")}
                onChange={(e) =>
                  setShippingPolicyId(e.target.value ? Number(e.target.value) : null)
                }
                disabled={policiesLoading}
                options={[
                  { value: "", label: policiesLoading ? "불러오는 중..." : "정책 선택" },
                  ...policies.shipping_policies.map((p) => ({
                    value: String(p.id),
                    label: p.label ?? p.name,
                  })),
                ]}
              />
            </Field>
            <Field label="반품/교환 정책">
              <Select
                value={String(returnPolicyId ?? "")}
                onChange={(e) => setReturnPolicyId(e.target.value ? Number(e.target.value) : null)}
                disabled={policiesLoading}
                options={[
                  { value: "", label: policiesLoading ? "불러오는 중..." : "정책 선택" },
                  ...policies.return_exchange_policies.map((p) => ({
                    value: String(p.id),
                    label: p.label ?? p.name,
                  })),
                ]}
              />
            </Field>
            <Field label="A/S 정책">
              <Select
                value={String(afterServiceId ?? "")}
                onChange={(e) => setAfterServiceId(e.target.value ? Number(e.target.value) : null)}
                disabled={policiesLoading}
                options={[
                  { value: "", label: policiesLoading ? "불러오는 중..." : "정책 선택" },
                  ...policies.after_service_policies.map((p) => ({
                    value: String(p.id),
                    label: p.label ?? p.name,
                  })),
                ]}
              />
            </Field>
          </div>
        </section>

        {/* ── 하단 버튼 ────────────────────────────────────────── */}
        <div className="pr-footer">
          <Button variant="danger" onClick={handleDelete}>
            삭제하기
          </Button>
          <div style={{ display: "flex", gap: 8 }}>
            <Button
              variant="primary"
              disabled={submitting || submitting}
              onClick={() => handleSubmit(false)}
            >
              {submitting ? "처리 중..." : "수정 완료"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductEdit;
